import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
// Force static service deployment - significant change
import CartResQDashboard from './components/CartResQDashboard';
import StoreSetup from './components/setup/StoreSetup';
import ShopifyCallback from './components/setup/ShopifyCallback';
import CartRecovery from './components/carts/CartRecovery';
import Pricing from './components/pricing/Pricing';
import Billing from './components/billing/Billing';
import AuthPage from './components/setup/Login';
import ResetPassword from './components/setup/ResetPassword';
import TestSubscription from './components/setup/TestSubscription';
import AbandonedCarts from './components/carts/AbandonedCarts';
import Customers from './components/customers/Customers';
import Campaigns from './components/campaigns/Campaigns';
import Notifications from './components/notifications/Notifications';
import Coupons from './components/coupons/Coupons';
import Analytics from './components/analytics/Analytics';
import Settings from './components/settings/Settings';
import PushNotificationSetup from './components/settings/PushNotificationSetup';
import DashboardLayout from './components/layout/DashboardLayout';
import NotFound from './components/NotFound';
import './App.css';
import api from './utils/api';
import { LoadingProvider } from './contexts/LoadingContext';
import { AuthProvider } from './contexts/AuthContext';
import GlobalLoadingOverlay from './components/common/GlobalLoadingOverlay';
import PageRefreshIndicator from './components/common/PageRefreshIndicator';
import TestPage from './components/TestPage';

// Landing page component that shows the static landing.html content
function LandingPage() {
  useEffect(() => {
    // Load the static landing page content
    fetch('/landing.html')
      .then(response => response.text())
      .then(html => {
        // Replace the current page content with the landing page
        document.open();
        document.write(html);
        document.close();
      })
      .catch(error => {
        console.error('Error loading landing page:', error);
      });
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading landing page...</p>
      </div>
    </div>
  );
}

function useAuthState() {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isStoreConnected, setIsStoreConnected] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      setIsAuthenticated(!!token);
      if (!token) {
        setAuthChecked(true);
        return;
      }
      try {
        const res = await api.get('/api/subscribe/status');
        const hasActiveSubscription = res.data.status === 'active' || 
                                   res.data.status === 'trialing' || 
                                   (res.data.plan && res.data.plan === 'free');
        setIsSubscribed(hasActiveSubscription);
        if (hasActiveSubscription) {
          try {
            const storeUrl = localStorage.getItem('storeUrl');
            const platform = localStorage.getItem('platform');
            const connectionRes = await api.get('/api/stores/check-connection', {
              params: {
                ...(storeUrl && { store_url: storeUrl }),
                ...(platform && { platform: platform })
              }
            });
            const hasConnection = connectionRes.data.hasConnection;
            setIsStoreConnected(hasConnection);
            if (hasConnection) {
              // Only update localStorage if it's not already set (to preserve user's selection)
              if (!localStorage.getItem('storeUrl')) {
                localStorage.setItem('storeUrl', connectionRes.data.store_url);
              }
              if (!localStorage.getItem('platform')) {
                localStorage.setItem('platform', connectionRes.data.platform);
              }
              localStorage.setItem('isConnected', 'true');
              localStorage.setItem('storeUrl', connectionRes.data.store_url);
            } else {
              localStorage.removeItem('isConnected');
              localStorage.removeItem('platform');
              localStorage.removeItem('storeUrl');
            }
          } catch (connectionErr) {
            setIsStoreConnected(false);
            localStorage.removeItem('isConnected');
            localStorage.removeItem('platform');
            localStorage.removeItem('storeUrl');
          }
        } else {
          setIsStoreConnected(false);
          localStorage.removeItem('isConnected');
          localStorage.removeItem('platform');
          localStorage.removeItem('storeUrl');
        }
      } catch (error) {
        setIsSubscribed(false);
        setIsStoreConnected(false);
        localStorage.removeItem('isConnected');
        localStorage.removeItem('platform');
        localStorage.removeItem('storeUrl');
      }
      setAuthChecked(true);
    };
    checkAuth();
  }, [location.pathname]);

  return { authChecked, isAuthenticated, isSubscribed, isStoreConnected };
}

function SmartRedirect() {
  const { authChecked, isAuthenticated, isSubscribed, isStoreConnected } = useAuthState();
  
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Only redirect from root path "/" - don't interfere with direct page access
  // If not authenticated, show landing page directly
  if (!isAuthenticated) {
    return <LandingPage />;
  }
  
  // If authenticated but not subscribed, redirect to pricing
  if (!isSubscribed) {
    return <Navigate to="/pricing" replace />;
  }
  
  // If authenticated and subscribed but not store connected, redirect to store setup
  if (!isStoreConnected) {
    return <Navigate to="/store-setup" replace />;
  }
  
  // If all conditions are met, redirect to dashboard
  return <Navigate to="/dashboard" replace />;
}

function ProtectedRoute({ children }) {
  const { authChecked, isAuthenticated, isSubscribed, isStoreConnected } = useAuthState();
  const location = useLocation();
  
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated: only allow /landing, /login, /register, /reset-password
  if (!isAuthenticated) {
    if (["/landing", "/login", "/register", "/reset-password"].includes(location.pathname)) {
      return children;
    }
    return <Navigate to="/landing" replace />;
  }

  // Authenticated: block /login, /register, /reset-password
  if (isAuthenticated && ["/login", "/register", "/reset-password"].includes(location.pathname)) {
    // Check if user has subscription and store connection
    if (isSubscribed && isStoreConnected) {
      return <Navigate to="/dashboard" replace />;
    } else if (isSubscribed && !isStoreConnected) {
      return <Navigate to="/store-setup" replace />;
    } else {
      return <Navigate to="/pricing" replace />;
    }
  }

  // Authenticated but not subscribed: allow /pricing, /store-setup, /push-setup, and /dashboard
  if (!isSubscribed) {
    if (["/pricing", "/store-setup", "/logout", "/push-setup", "/dashboard"].includes(location.pathname)) {
      return children;
    }
    return <Navigate to="/pricing" replace />;
  }

  // Authenticated and subscribed but not store connected: allow /store-setup and /push-setup
  if (!isStoreConnected) {
    if (["/store-setup", "/push-setup"].includes(location.pathname)) {
      return children;
    }
    return <Navigate to="/store-setup" replace />;
  }

  // Authenticated, subscribed, and store connected: block /store-setup but allow /push-setup
  if (isStoreConnected && location.pathname === '/store-setup') {
    return <Navigate to="/dashboard" replace />;
  }

  // All good - allow access to the page
  return children;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <LoadingProvider>
          <div className="App">
            <PageRefreshIndicator />
            <GlobalLoadingOverlay />
            <AppRoutes />
          </div>
        </LoadingProvider>
      </AuthProvider>
    </Router>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/login" element={<ProtectedRoute><AuthPage /></ProtectedRoute>} />
      <Route path="/register" element={<ProtectedRoute><AuthPage /></ProtectedRoute>} />
      <Route path="/reset-password" element={<ProtectedRoute><ResetPassword /></ProtectedRoute>} />
      <Route path="/test-subscription" element={<ProtectedRoute><TestSubscription /></ProtectedRoute>} />
      <Route path="/cart/recover" element={<ProtectedRoute><CartRecovery /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/store-setup" element={<ProtectedRoute><StoreSetup /></ProtectedRoute>} />
      <Route path="/setup/shopify-callback" element={<ProtectedRoute><ShopifyCallback /></ProtectedRoute>} />
      <Route path="/test" element={<TestPage />} />

      {/* Main app pages with sidebar/header */}
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<CartResQDashboard />} />
        <Route path="/carts" element={<AbandonedCarts />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/coupons" element={<Coupons />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/push-setup" element={<PushNotificationSetup />} />
      </Route>

      <Route path="/" element={<SmartRedirect />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;

